import React, { useState, useEffect } from 'react';
import { getRole } from '../../utils/role';
import axiosInstance from '../../utils/axios';
import Wrapper from '../../components/Wrapper';

const ProductOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState({});

  const userRole = getRole();
  const statuses = ['PENDING', 'CONFIRMED', 'SHIPPED', 'DELIVERED', 'CANCELLED'];

  const getApiBaseUrl = () => {
    switch (userRole) {
      case 'SUPPORT': return '/super-admin/orders';
      case 'PARTNER': return '/admin/orders';
      case 'TEAM_MEMBER': return '/admin/orders';
      case 'FIELD_OFFICER': return '/field-officer/orders';
      default: return null;
    }
  };

  const fetchOrders = async (filter) => {
    setIsLoading(true);
    setError(null);
    try {
      const baseUrl = getApiBaseUrl();
      if (!baseUrl) throw new Error('Unauthorized or invalid role setup.');

      const url = filter ? `${baseUrl}?status=${filter}` : baseUrl;
      const response = await axiosInstance.get(url);
      
      setOrders(response.data.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || 'Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders(statusFilter);
  }, [statusFilter]);

  const handleStatusChange = async (orderId, newStatus) => {
    if (!window.confirm(`Are you sure you want to change this order status to ${newStatus}?`)) return;
    
    setUpdatingId(orderId);
    try {
      const baseUrl = getApiBaseUrl();
      await axiosInstance.patch(`${baseUrl}/${orderId}/status`, { status: newStatus });
      
      setOrders(orders.map(order => 
        order.order_id === orderId ? { ...order, status: newStatus } : order
      ));
    } catch (err) {
      alert(err.response?.data?.msg || 'Failed to update status. Please try again.');
    } finally {
      setUpdatingId(null);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'PENDING': return 'bg-warning text-dark';
      case 'CONFIRMED': return 'bg-info text-dark';
      case 'SHIPPED': return 'bg-primary';
      case 'DELIVERED': return 'bg-success';
      case 'CANCELLED': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  const toggleOrderItems = (orderId) => {
    setExpandedOrders(prev => ({
      ...prev,
      [orderId]: !prev[orderId]
    }));
  };

  return (
    <Wrapper>
      <div className="d-flex justify-content-between align-items-center mb-4 mt-2">
        <h4 className="m-0 fw-bold">Order Management</h4>
        
        <div className="d-flex align-items-center">
          <label className="me-2 fw-semibold text-secondary mb-0">Filter:</label>
          <select 
            className="form-select form-select-sm" 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ width: '150px' }}
          >
            <option value="">All Orders</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger py-2" role="alert">
          {error}
        </div>
      )}

      <div className="card shadow-sm border-0">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-4">Order ID & Date</th>
                  <th>Customer Info</th>
                  {userRole === 'SUPPORT' && <th>Fulfilling Partner</th>}
                  <th>Order Items</th>
                  <th>Status</th>
                  <th className="text-end pe-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={userRole === 'SUPPORT' ? "6" : "5"} className="text-center py-5 text-secondary">
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Loading orders...
                    </td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={userRole === 'SUPPORT' ? "6" : "5"} className="text-center py-5 text-secondary">
                      No orders found for the selected filter.
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => {
                    const isExpanded = expandedOrders[order.order_id];
                    const itemCount = order.items?.length || 0;
                    
                    return (
                      <tr key={order.order_id}>
                        <td className="ps-4">
                          <div className="fw-bold text-primary">{order.order_id}</div>
                          <div className="text-muted small">
                            {new Date(order.order_date).toLocaleDateString('en-IN', {
                              day: 'numeric', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        </td>
                        <td>
                          <div className="fw-semibold">{order.first_name} {order.last_name}</div>
                          <div className="text-muted small mb-1">{order.mobile_no}</div>
                          <div className="text-secondary" style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                            {order.address}<br />
                            {order.village ? `${order.village}, ` : ''}{order.city}<br />
                            {order.state} - {order.pincode}
                          </div>
                        </td>
                        
                        {userRole === 'SUPPORT' && (
                          <td>
                            <div className="fw-semibold">{order.partner_name || 'N/A'}</div>
                            <div className="text-muted small">
                              {order.partner_contact || 'No contact info'}
                            </div>
                          </td>
                        )}

                        <td>
                          <div className="d-flex align-items-center">
                            <span className="fw-semibold me-3">
                              {itemCount} {itemCount === 1 ? 'Item' : 'Items'}
                            </span>
                            {itemCount > 0 && (
                              <button 
                                className="btn btn-sm btn-outline-secondary py-0 px-2"
                                style={{ fontSize: '0.8rem' }}
                                onClick={() => toggleOrderItems(order.order_id)}
                              >
                                {isExpanded ? 'Hide' : 'View'}
                              </button>
                            )}
                          </div>
                          
                          {isExpanded && (
                            <div className="mt-3 p-2 bg-light rounded border" style={{ minWidth: '250px' }}>
                              {order.items?.map((item, index) => (
                                <div key={item.item_id} className={index > 0 ? "mt-2 pt-2 border-top" : ""}>
                                  <div className="fw-semibold text-dark">{item.product_name}</div>
                                  <div className="text-muted small">
                                    {item.company} • {item.pack_size} <span className="fw-bold text-dark ms-2">Qty: {item.quantity}</span>
                                  </div>
                                  
                                  {(item.product_admin_name || item.product_admin_contact) && (
                                    <div className="mt-1 d-inline-block rounded px-2 py-1" style={{ backgroundColor: '#e9ecef', fontSize: '0.75rem' }}>
                                      <span className="text-secondary me-1">Sourced from:</span>
                                      <span className="fw-semibold text-dark">{item.product_admin_name || 'Partner'}</span>
                                      {item.product_admin_contact && <span className="ms-1 text-muted">({item.product_admin_contact})</span>}
                                    </div>
                                  )}
                                  {/* ----------------------------------------------------------- */}

                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="text-end pe-4">
                          <select
                            className="form-select form-select-sm d-inline-block"
                            style={{ width: '130px' }}
                            value={order.status}
                            onChange={(e) => handleStatusChange(order.order_id, e.target.value)}
                            disabled={updatingId === order.order_id || order.status === 'CANCELLED' || order.status === 'DELIVERED'}
                          >
                            <option value="" disabled>Update Status</option>
                            {statuses.map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default ProductOrdersPage;